import { ref, computed, onUnmounted, onMounted, watch, type Ref } from 'vue'

export interface UseServerSentEventsOptions<T = any> {
  /**
   * Автоматически подключаться при создании
   */
  immediate?: boolean

  /**
   * Автоматически переподключаться при разрыве
   */
  autoReconnect?: boolean

  /**
   * Задержка перед переподключением (мс)
   */
  reconnectInterval?: number

  /**
   * Максимальное количество попыток переподключения
   */
  maxReconnectAttempts?: number

  /**
   * Формат данных (json или text)
   */
  format?: 'json' | 'text'

  /**
   * Функция для преобразования данных
   */
  transform?: (data: any) => T

  /**
   * Дополнительные заголовки
   * Важно: стандартный EventSource не поддерживает кастомные заголовки,
   * поэтому реализована альтернативная версия с fetch
   */
  headers?: Record<string, string> | (() => Record<string, string>)

  /**
   * Передавать cookies
   */
  withCredentials?: boolean

  /**
   * Метод запроса (GET по умолчанию для SSE)
   */
  method?: 'GET' | 'POST'

  /**
   * Тело запроса (для POST запросов)
   */
  body?: any

  /**
   * Использовать полифилл для поддержки заголовков
   * (через fetch API вместо нативного EventSource)
   */
  useFetchPolyfill?: boolean

  /**
   * Таймаут соединения (мс)
   */
  timeout?: number

  /**
   * Включить режим отладки
   */
  debug?: boolean
}

export interface ServerSentEvent<T = any> {
  id?: string
  event: string
  data: T
  raw: MessageEvent
  retry?: number
  signals?: any
}

export interface SSECloseEvent extends Event {
  code?: number
  reason?: string
}

class EventSourceWithHeaders {
  private url: string
  private options: UseServerSentEventsOptions
  private controller: AbortController | null = null
  private eventSource: EventSource | null = null
  private listeners: Map<string, EventListener[]> = new Map()
  private retryDelay: number = 1000
  private isUsingNative: boolean = false
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 5
  private lastEventId: string = ''

  onopen: ((event: Event) => void) | null = null
  onmessage: ((event: MessageEvent) => void) | null = null
  onerror: ((event: Event) => void) | null = null

  constructor(url: string, options: UseServerSentEventsOptions = {}) {
    this.url = url
    this.options = options
    this.maxReconnectAttempts = options.maxReconnectAttempts || 5
    this.retryDelay = options.reconnectInterval || 3000

    this.isUsingNative = this.canUseNativeEventSource()

    if (this.isUsingNative && (!options.headers || Object.keys(options.headers).length === 0)) {
      this.createNativeConnection()
    } else {
      this.createFetchConnection()
    }
  }

  private canUseNativeEventSource(): boolean {
    if (this.options.headers && Object.keys(this.options.headers).length > 0) {
      return false
    }
    return typeof EventSource !== 'undefined'
  }

  private createNativeConnection() {
    try {
      const eventSourceInit: any = {}
      if (this.options.withCredentials !== undefined) {
        eventSourceInit.withCredentials = this.options.withCredentials
      }

      this.eventSource = new EventSource(this.url, eventSourceInit)

      this.eventSource.onopen = (event) => {
        this.reconnectAttempts = 0
        if (this.onopen) this.onopen(event)
        this.dispatchEvent('open', event)
      }

      this.eventSource.onmessage = (event) => {
        if (this.onmessage) this.onmessage(event)
        this.dispatchEvent('message', event)
      }

      this.eventSource.onerror = (event) => {
        if (this.onerror) this.onerror(event)
        this.dispatchEvent('error', event)

        if (
          this.options.autoReconnect !== false &&
          this.reconnectAttempts < this.maxReconnectAttempts
        ) {
          setTimeout(() => {
            this.reconnectAttempts++
            this.close()
            this.createNativeConnection()
          }, this.retryDelay * this.reconnectAttempts)
        }
      }

      if (this.listeners.size > 0) {
        this.listeners.forEach((_, eventName) => {
          if (eventName !== 'open' && eventName !== 'message' && eventName !== 'error') {
            this.eventSource!.addEventListener(eventName, (event: any) => {
              this.dispatchEvent(eventName, event)
            })
          }
        })
      }
    } catch (error) {
      console.error('Failed to create native EventSource:', error)
      this.createFetchConnection()
    }
  }

  private async createFetchConnection() {
    try {
      this.controller = new AbortController()
      const signal = this.controller.signal

      const headers = new Headers()

      headers.set('Accept', 'text/event-stream')
      headers.set('Cache-Control', 'no-cache')
      headers.set('Connection', 'keep-alive')
      headers.set('Content-Type', 'text/event-stream')
      headers.set('Cache-Control', 'no-cache')
      headers.set('Access-Control-Allow-Origin', '*')
      headers.set('Access-Control-Allow-Credentials', 'true')

      if (this.options.headers) {
        const headerObj =
          typeof this.options.headers === 'function' ? this.options.headers() : this.options.headers

        Object.entries(headerObj).forEach(([key, value]) => {
          headers.set(key, value)
        })
      }

      if (this.lastEventId) {
        headers.set('Last-Event-ID', this.lastEventId)
      }

      const init: RequestInit = {
        method: this.options.method || 'GET',
        headers,
        signal,
        credentials: this.options.withCredentials ? 'include' : 'same-origin',
      }

      if (this.options.body && this.options.method === 'POST') {
        init.body =
          typeof this.options.body === 'string'
            ? this.options.body
            : JSON.stringify(this.options.body)
        headers.set('Content-Type', 'application/json')
      }

      const response = await fetch(this.url, init)

      if (!response.ok || !response.body) {
        throw new Error(`SSE request failed: ${response.status} ${response.statusText}`)
      }

      this.reconnectAttempts = 0

      const openEvent = new Event('open')
      if (this.onopen) this.onopen(openEvent)
      this.dispatchEvent('open', openEvent)

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      const readStream = async () => {
        try {
          while (true) {
            const { done, value } = await reader.read()

            if (done) {
              const closeEvent = new Event('close')
              this.dispatchEvent('close', closeEvent)
              break
            }

            buffer += decoder.decode(value, { stream: true })

            const events = this.parseSSE(buffer)

            if (events.remainingBuffer) {
              buffer = events.remainingBuffer
            } else {
              buffer = ''
            }

            events.events.forEach((event) => {
              this.processSSEEvent(event)
            })
          }
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('SSE stream error:', error)

            const errorEvent = new Event('error')
            if (this.onerror) this.onerror(errorEvent)
            this.dispatchEvent('error', errorEvent)

            if (
              this.options.autoReconnect !== false &&
              this.reconnectAttempts < this.maxReconnectAttempts
            ) {
              setTimeout(() => {
                this.reconnectAttempts++
                this.close()
                this.createFetchConnection()
              }, this.retryDelay * this.reconnectAttempts)
            }
          }
        }
      }

      readStream()
    } catch (error) {
      const errorEvent = new Event('error')
      if (this.onerror) this.onerror(errorEvent)
      this.dispatchEvent('error', errorEvent)

      if (
        this.options.autoReconnect !== false &&
        this.reconnectAttempts < this.maxReconnectAttempts
      ) {
        setTimeout(() => {
          this.reconnectAttempts++
          this.createFetchConnection()
        }, this.retryDelay * this.reconnectAttempts)
      }
    }
  }

  private parseSSE(buffer: string): {
    events: any[]
    remainingBuffer: string
  } {
    const events = []
    let remainingBuffer = buffer

    while (true) {
      const eventEnd = remainingBuffer.indexOf('\n\n')

      if (eventEnd === -1) {
        break
      }

      const eventData = remainingBuffer.substring(0, eventEnd)
      remainingBuffer = remainingBuffer.substring(eventEnd + 2)

      const lines = eventData.split('\n')
      const event: any = { data: '' }

      lines.forEach((line) => {
        if (line.startsWith('id:')) {
          event.id = line.substring(3).trim()
          this.lastEventId = event.id
        } else if (line.startsWith('event:')) {
          event.event = line.substring(6).trim() || 'message'
        } else if (line.startsWith('data:')) {
          event.data += line.substring(5).trim() + '\n'
        } else if (line.startsWith('retry:')) {
          const retry = parseInt(line.substring(6).trim(), 10)
          if (!isNaN(retry)) {
            this.retryDelay = retry
          }
        } else if (line.startsWith(':')) {
        }
      })

      if (event.data.endsWith('\n')) {
        event.data = event.data.slice(0, -1)
      }

      events.push(event)
    }

    return { events, remainingBuffer }
  }

  private processSSEEvent(sseEvent: any) {
    const messageEvent = new MessageEvent(sseEvent.event || 'message', {
      data: sseEvent.data,
      lastEventId: sseEvent.id || '',
    })

    if (this.onmessage) {
      this.onmessage(messageEvent)
    }
    this.dispatchEvent('message', messageEvent)

    if (sseEvent.event && sseEvent.event !== 'message') {
      const customEvent = new MessageEvent(sseEvent.event, {
        data: sseEvent.data,
        lastEventId: sseEvent.id || '',
      })
      this.dispatchEvent(sseEvent.event, customEvent)
    }
  }

  addEventListener(event: string, listener: EventListener) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, [])
    }
    this.listeners.get(event)!.push(listener)

    if (
      this.isUsingNative &&
      this.eventSource &&
      event !== 'open' &&
      event !== 'message' &&
      event !== 'error'
    ) {
      this.eventSource.addEventListener(event, listener)
    }
  }

  removeEventListener(event: string, listener: EventListener) {
    const listeners = this.listeners.get(event)
    if (listeners) {
      const index = listeners.indexOf(listener)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }

    if (this.isUsingNative && this.eventSource) {
      this.eventSource.removeEventListener(event, listener)
    }
  }

  dispatchEvent(eventName: string, event: Event) {
    const listeners = this.listeners.get(eventName)
    if (listeners) {
      listeners.forEach((listener) => {
        try {
          listener(event)
        } catch (error) {
          console.error(`Error in ${eventName} event listener:`, error)
        }
      })
    }
  }

  close() {
    if (this.isUsingNative && this.eventSource) {
      this.eventSource.close()
      this.eventSource = null
    }

    if (this.controller) {
      this.controller.abort()
      this.controller = null
    }

    const closeEvent = new Event('close')
    this.dispatchEvent('close', closeEvent)
  }
}

export function useServerSentEvents<T = any>(
  url: string | Ref<string> | (() => string),
  options: UseServerSentEventsOptions<T> = {},
): any {
  const {
    immediate = true,
    autoReconnect = true,
    reconnectInterval = 3000,
    maxReconnectAttempts = 5,
    format = 'json',
    withCredentials = false,
    transform,
    headers = {},
    method = 'GET',
    body,
    useFetchPolyfill = false,
    timeout,
    debug = false,
  } = options

  const data: Ref<T | null> = ref(null)
  const lastEvent: Ref<ServerSentEvent<T> | null> = ref(null)
  const eventSource: Ref<EventSourceWithHeaders | EventSource | null> = ref(null)
  const isConnected = ref(false)
  const isConnecting = ref(false)
  const error = ref<Error | Event | null>(null)
  const reconnectAttempts = ref(0)
  const connectionId = ref<string>('')
  const signals = ref<any>()

  const events: Ref<ServerSentEvent<T>[]> = ref([])

  const eventHandlers: Map<string, (event: ServerSentEvent<T>) => void> = new Map()

  const generateConnectionId = () => {
    return `sse-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  const getUrl = (): string => {
    if (typeof url === 'function') return url()
    if (typeof url === 'object' && 'value' in url) return url.value
    return url as string
  }

  const getHeaders = (): Record<string, string> => {
    if (typeof headers === 'function') {
      return headers()
    }
    return { ...headers }
  }

  const onError = (event: any) => {
    const eventHandler = eventHandlers.get('error')
    if (eventHandler) {
      eventHandler({ ...event, signals: signals?.value })
    }
  }

  const onOpen = (event: any) => {
    const eventHandler = eventHandlers.get('open')
    if (eventHandler) {
      eventHandler({ ...event, signals: signals?.value })
    }
  }

  const onClose = (event: any) => {
    const eventHandler = eventHandlers.get('close')
    if (eventHandler) {
      eventHandler({ ...event, signals: signals?.value })
    }
  }

  const createEventSource = () => {
    if (eventSource.value) {
      closeEventSource()
    }

    const sourceUrl = getUrl()
    connectionId.value = generateConnectionId()
    isConnecting.value = true
    error.value = null


    try {
      const currentHeaders = getHeaders()
      const shouldUsePolyfill = useFetchPolyfill || Object.keys(currentHeaders).length > 0

      if (shouldUsePolyfill) {
        const es = new EventSourceWithHeaders(sourceUrl, {
          ...options,
          headers: currentHeaders,
          method,
          body,
          withCredentials,
          autoReconnect,
          maxReconnectAttempts,
          reconnectInterval,
        })

        eventSource.value = es
        setupPolyfillEventSource(es)
      } else {
        const es = new EventSource(sourceUrl, { withCredentials })
        eventSource.value = es
        setupNativeEventSource(es)
      }

      if (timeout) {
        setTimeout(() => {
          if (isConnecting.value && !isConnected.value) {
            const timeoutError = new Error(`Connection timeout after ${timeout}ms`)
            error.value = timeoutError
            onError?.(timeoutError)
            closeEventSource()

            if (autoReconnect) {
              scheduleReconnect()
            }
          }
        }, timeout)
      }
    } catch (err) {
      isConnecting.value = false
      const errorObj = err instanceof Error ? err : new Error(String(err))
      error.value = errorObj
      onError?.(errorObj)

      if (autoReconnect) {
        scheduleReconnect()
      }
    }
  }

  const setupNativeEventSource = (es: EventSource) => {
    es.onopen = (event) => {
      isConnected.value = true
      isConnecting.value = false
      reconnectAttempts.value = 0
      onOpen(event)
    }

    es.onmessage = (rawEvent) => {
      handleSSEMessage(rawEvent)
    }

    es.onerror = (err) => {
      isConnected.value = false
      isConnecting.value = false
      error.value = err
      onError(err)

      if (autoReconnect && reconnectAttempts.value < maxReconnectAttempts) {
        scheduleReconnect()
      }
    }

    eventHandlers.forEach((_, eventName) => {
      if (eventName !== 'message') {
        es.addEventListener(eventName, (rawEvent: MessageEvent) => {
          handleSSEMessage(rawEvent, eventName)
        })
      }
    })
  }

  const setupPolyfillEventSource = (es: EventSourceWithHeaders) => {
    es.onopen = (event) => {
      isConnected.value = true
      isConnecting.value = false
      reconnectAttempts.value = 0
      onOpen(event)
    }

    es.onmessage = (rawEvent) => {
      handleSSEMessage(rawEvent, rawEvent.type)
    }

    es.onerror = (err) => {
      isConnected.value = false
      isConnecting.value = false
      error.value = err
      onError(err)

      if (autoReconnect && reconnectAttempts.value < maxReconnectAttempts) {
        scheduleReconnect()
      }
    }

    es.addEventListener('close', () => {
      isConnected.value = false
      const closeEvent = new Event('close')
      onClose(closeEvent)
    })
  }

  const handleSSEMessage = (rawEvent: MessageEvent, eventName: string = 'message') => {
    try {

      const parsedData =
        format === 'json' && rawEvent.data ? JSON.parse(rawEvent.data) : rawEvent.data

      const transformedData = transform ? transform(parsedData) : parsedData

      const sseEvent: ServerSentEvent<T> = {
        id: rawEvent.lastEventId,
        event: eventName,
        data: transformedData,
        raw: rawEvent,
        signals: signals.value,
      }

      data.value = transformedData
      lastEvent.value = sseEvent
      events.value.push(sseEvent)

      const eventHandler = eventHandlers.get(eventName)
      if (eventHandler) {
        eventHandler(sseEvent)
      }
    } catch (err) {
      const parseError = err instanceof Error ? err : new Error(String(err))
      console.error('Failed to parse SSE message:', parseError)
      error.value = parseError
    }
  }

  const scheduleReconnect = () => {
    if (reconnectAttempts.value >= maxReconnectAttempts) {
      return
    }

    reconnectAttempts.value++
    const delay = reconnectInterval * reconnectAttempts.value

    setTimeout(() => {
      if (!isConnected.value && !isConnecting.value) {
        connect()
      }
    }, delay)
  }

  const closeEventSource = () => {
    if (eventSource.value) {
      if ('close' in eventSource.value) {
        eventSource.value.close()
      }

      eventSource.value = null
    }
    isConnected.value = false
    isConnecting.value = false
  }

  const connect = () => {
    if (isConnecting.value || isConnected.value) {
      return
    }

    createEventSource()
  }

  const disconnect = () => {
    closeEventSource()

    if (onClose) {
      const closeEvent = new Event('close')
      onClose(closeEvent)
    }
  }

  const reconnect = () => {
    reconnectAttempts.value = 0
    disconnect()
    setTimeout(() => connect(), 100)
  }

  const on = (eventName: string, handler: (event: ServerSentEvent<T>) => void) => {
    eventHandlers.set(eventName, handler)

    if (eventSource.value) {
      if (eventSource.value instanceof EventSource) {
        if (eventName !== 'message') {
          eventSource.value.addEventListener(eventName, (rawEvent: MessageEvent) => {
            handleSSEMessage(rawEvent, eventName)
          })
        }
      }
    }

    return () => {
      eventHandlers.delete(eventName)

      if (eventSource.value) {
        if (eventSource.value instanceof EventSource) {
          eventSource.value.removeEventListener(eventName, () => {})
        } else if ('removeEventListener' in eventSource.value) {
          eventSource.value.removeEventListener(eventName, () => {})
        }
      }
    }
  }

  const send = async (sendData: any, endpoint?: string) => {
    try {
      const sendUrl = endpoint || getUrl()
      const currentHeaders = getHeaders()


      const response = await fetch(sendUrl, {
        method: 'POST',
        headers: currentHeaders,
        body: sendData,
        credentials: withCredentials ? 'include' : 'same-origin',
      })

      return response
    } catch (err) {
      throw err
    }
  }

  const clearEvents = () => {
    events.value = []
  }

  const getStats = () => {
    return {
      connectionId: connectionId.value,
      isConnected: isConnected.value,
      isConnecting: isConnecting.value,
      reconnectAttempts: reconnectAttempts.value,
      totalEvents: events.value.length,
      lastEvent: lastEvent.value,
      url: getUrl(),
    }
  }

  if (immediate) {
    onMounted(() => {
      connect()
    })
  }

  onUnmounted(() => {
    disconnect()
  })

  if (typeof url === 'object' && 'value' in url) {
    watch(url, (newUrl) => {
      if (isConnected.value || isConnecting.value) {
        disconnect()
        connect()
      }
    })
  }

  if (typeof headers === 'function') {
  }

  const setupSignal = (sgnl: any) => (signals.value = sgnl)

  return {
    data,
    lastEvent: lastEvent,
    events: events,
    isConnected: isConnected,
    isConnecting: isConnecting,
    error: error,
    reconnectAttempts: reconnectAttempts,
    connectionId: connectionId,

    connect,
    disconnect,
    reconnect,
    on,
    send,
    clearEvents,
    getStats,

    setupSignal,

    hasError: computed(() => error.value !== null),
    isActive: computed(() => isConnected.value || isConnecting.value),
    totalEvents: computed(() => events.value.length),
  }
}
